/**
 * "Skip the agent list when the account has exactly one agent."
 *
 * The sign-in entry points land on /my-bots because none of them knows how many
 * agents the account has — the bot list is fetched later, by
 * SelectedBotProvider. So each entry point ARMS a one-shot flag here, and
 * <SingleAgentEntryGate /> consumes it once the list has loaded: exactly one
 * agent → straight into that agent (/overview), no one-card list in between.
 *
 * Why a flag and not simply "redirect /my-bots whenever bots.length === 1":
 * /my-bots must stay reachable on purpose. It is the sidebar's Agents entry and
 * it HOSTS the add-agent modal (`triggerAddBotModal`) — an unconditional
 * redirect would make creating a second agent impossible.
 *
 * sessionStorage, not router location state: some entry points hard-navigate
 * (`window.location.href = '/my-bots'`, WixOnboarding.js), which drops router
 * state but keeps sessionStorage. Scoped to the tab, gone when it closes.
 */
const ENTRY_FLAG = "ub:autoEnterSingleAgent";

// Called by a sign-in entry point right before it navigates to /my-bots.
export const markSingleAgentEntry = () => {
  try {
    sessionStorage.setItem(ENTRY_FLAG, "1");
  } catch (_) {}
};

// Read WITHOUT clearing. The gate peeks on every render to decide whether to
// hold its loader; it must not spend the flag there, because AppShell tears the
// whole layout down and back up right after sign-in (the validate_token effect
// re-runs on isAuthenticated and flips `loading`, App.js:601,:644). A flag spent
// in the discarded mount would leave the remount with nothing to act on — which
// is exactly how the skip silently stopped working.
export const isSingleAgentEntryPending = () => {
  try {
    return sessionStorage.getItem(ENTRY_FLAG) === "1";
  } catch (_) {
    return false;
  }
};

// One-shot read: consuming also clears the flag. Call it only at the moment the
// decision is actually made (bot list loaded), never merely on mount, so it
// survives the remount above. Every later navigation to /my-bots (sidebar,
// add-agent, back-to-agents) then finds nothing and is left alone.
export const consumeSingleAgentEntry = () => {
  try {
    const armed = sessionStorage.getItem(ENTRY_FLAG) === "1";
    sessionStorage.removeItem(ENTRY_FLAG);
    return armed;
  } catch (_) {
    return false;
  }
};

// Pure decision, given an already-loaded bot list. Callers must have consumed
// the entry flag first (and must wait for `loading === false`, or a mid-fetch
// empty list would read as "no single agent").
export const shouldEnterSingleAgent = ({ pathname, bots }) => {
  if (pathname !== "/my-bots") return false;
  if (!Array.isArray(bots) || bots.length !== 1) return false;
  // The add-agent flow parks the user on /my-bots on purpose to open
  // AddBotModal — never redirect out from under it.
  try {
    if (localStorage.getItem("triggerAddBotModal") === "1") return false;
  } catch (_) {}
  return true;
};
